import React, { useState, useEffect, useCallback } from 'react';
import { authService } from '../services';
import websocketService from '../services/websocketService';

/**
 * B站账号绑定组件
 * 用户通过在指定直播间发送验证码来绑定B站账号
 */
function BilibiliBinding({ userId }) {
  const [status, setStatus] = useState(null); // null: loading, object: status data
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [copied, setCopied] = useState(false); // 复制状态

  // 复制验证码到剪贴板
  const copyVerifyCode = useCallback(async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('复制失败:', err);
    }
  }, []);

  // 加载绑定状态
  const loadBindingStatus = useCallback(async () => {
    try {
      const response = await fetch('/api/bilibili-binding/status', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      setStatus(data);
      
      // 如果有待验证的验证码，计算剩余时间
      if (data.pending && data.expires_at) {
        const expiresAt = new Date(data.expires_at).getTime();
        const remaining = Math.max(0, Math.floor((expiresAt - Date.now()) / 1000));
        setCountdown(remaining);
      }
    } catch (err) {
      console.error('加载绑定状态失败:', err);
      setError('加载绑定状态失败');
    } finally {
      setLoading(false);
    }
  }, []);

  // 请求绑定
  const requestBinding = async () => {
    setRequesting(true);
    setError('');
    setSuccess('');
    
    try {
      const response = await fetch('/api/bilibili-binding/request', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        setError(data.message || '请求绑定失败');
        return;
      }
      
      // 更新状态
      setStatus({
        bound: false,
        pending: true,
        verify_code: data.verify_code,
        verify_room_id: data.verify_room_id,
        expires_at: data.expires_at
      });
      setCountdown(data.expires_in);
      
      // 自动复制验证码
      copyVerifyCode(data.verify_code);
      
    } catch (err) {
      console.error('请求绑定失败:', err);
      setError('请求绑定失败，请稍后重试');
    } finally {
      setRequesting(false);
    }
  };

  // 解绑
  const unbind = async () => {
    if (!window.confirm('确定要解绑B站账号吗？')) {
      return;
    }
    
    try {
      const response = await fetch('/api/bilibili-binding', {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        setStatus({ bound: false, pending: false });
        setSuccess('解绑成功');
      } else {
        const data = await response.json();
        setError(data.message || '解绑失败');
      }
    } catch (err) {
      console.error('解绑失败:', err);
      setError('解绑失败，请稍后重试');
    }
  };

  // 初始化
  useEffect(() => {
    loadBindingStatus();
  }, [loadBindingStatus]);

  // 倒计时
  useEffect(() => {
    if (countdown <= 0) return;
    
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          // 验证码过期，刷新状态
          loadBindingStatus();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [countdown, loadBindingStatus]);

  // WebSocket 监听验证成功事件
  useEffect(() => {
    // 连接 WebSocket
    websocketService.connect();
    
    // 认证用户身份
    if (userId) {
      websocketService.authenticate(userId);
    }
    
    // 监听验证成功事件
    const handleVerifySuccess = (data) => {
      console.log('收到验证成功事件:', data);
      // 检查是否是当前用户的验证
      if (data.data && data.data.user_id === userId) {
        // 构建成功消息
        let successMessage = '🎉 B站账号绑定成功！';
        
        // 检查是否有自动升级
        const promotion = data.data.auto_promotion;
        if (promotion && promotion.promoted) {
          successMessage += `\n\n🎊 恭喜！您已自动获得「主播」身份！\n`;
          successMessage += `• 房间配额：${promotion.max_rooms} 个\n`;
          successMessage += `• QQ群配额：${promotion.max_qq_groups} 个\n`;
          if (promotion.room_bound) {
            successMessage += `• 已自动绑定您的直播间`;
          }
        }
        
        setSuccess(successMessage);
        setStatus({
          bound: true,
          pending: false,
          bilibili_uid: data.data.bilibili_uid,
          bilibili_uname: data.data.bilibili_uname,
          bilibili_face: data.data.bilibili_face
        });
        setCountdown(0);
      }
    };
    
    const unsubscribe = websocketService.on('bilibili_verify_success', handleVerifySuccess);
    
    return () => {
      unsubscribe();
    };
  }, [userId]);

  if (loading) {
    return (
      <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-light)' }}>
        加载中...
      </div>
    );
  }

  // 样式定义
  const cardStyle = {
    padding: '1.5rem',
    backgroundColor: 'var(--bg-light)',
    borderRadius: '12px',
    border: '1px solid var(--bg-purple-light)',
    marginTop: '1rem'
  };

  const labelStyle = {
    fontSize: '0.875rem',
    color: 'var(--text-light)',
    marginBottom: '0.5rem',
    display: 'flex',
    alignItems: 'center'
  };

  const valueStyle = {
    fontSize: '1.125rem',
    fontWeight: '600',
    color: 'var(--text-dark)'
  };

  const buttonStyle = {
    padding: '0.75rem 1.5rem',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: '600',
    transition: 'all 0.2s'
  };

  return (
    <div style={cardStyle}>
      <h3 style={{ 
        margin: '0 0 1rem 0', 
        color: 'var(--primary-purple)',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
      }}>
        <span>📺</span> B站账号绑定
      </h3>

      {error && (
        <div style={{ 
          padding: '0.75rem', 
          backgroundColor: '#fee', 
          border: '1px solid #fcc',
          borderRadius: '8px',
          color: '#c00',
          marginBottom: '1rem'
        }}>
          {error}
        </div>
      )}

      {success && (
        <div style={{ 
          padding: '0.75rem', 
          backgroundColor: '#efe', 
          border: '1px solid #cfc',
          borderRadius: '8px',
          color: '#080',
          marginBottom: '1rem'
        }}>
          {success}
        </div>
      )}

      {/* 已绑定状态 */}
      {status?.bound && (
        <div>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '1rem',
            padding: '1rem',
            backgroundColor: 'var(--bg-purple-light)',
            borderRadius: '8px',
            marginBottom: '1rem'
          }}>
            {status.bilibili_face && (
              <img 
                src={status.bilibili_face} 
                alt="头像"
                referrerPolicy="no-referrer"
                style={{ 
                  width: '48px', 
                  height: '48px', 
                  borderRadius: '50%',
                  border: '2px solid var(--primary-purple)'
                }}
              />
            )}
            <div>
              <div style={labelStyle}>已绑定B站账号</div>
              <div style={valueStyle}>
                {status.bilibili_uname} 
                <span style={{ 
                  fontSize: '0.875rem', 
                  color: 'var(--text-light)',
                  marginLeft: '0.5rem'
                }}>
                  (UID: {status.bilibili_uid})
                </span>
              </div>
            </div>
          </div>
          
          <button 
            onClick={unbind}
            style={{
              ...buttonStyle,
              backgroundColor: '#f5f5f5',
              color: '#666'
            }}
          >
            解除绑定
          </button>
        </div>
      )}

      {/* 待验证状态 */}
      {status?.pending && !status?.bound && (
        <div>
          <div style={{ 
            padding: '1rem',
            backgroundColor: '#fff8e1',
            border: '1px solid #ffcc02',
            borderRadius: '8px',
            marginBottom: '1rem'
          }}>
            <div style={{ marginBottom: '1rem' }}>
              <div style={labelStyle}>
                <span style={{ marginRight: '0.5rem' }}>🔑</span>
                验证码 {copied && <span style={{ color: '#27ae60', marginLeft: '0.5rem' }}>✓ 已复制</span>}
              </div>
              <div 
                onClick={() => copyVerifyCode(status.verify_code)}
                style={{ 
                  fontSize: '2.5rem', 
                  fontWeight: 'bold', 
                  color: 'var(--primary-purple)',
                  fontFamily: '"SF Mono", "Fira Code", "Consolas", "Monaco", monospace',
                  letterSpacing: '0.5rem',
                  cursor: 'pointer',
                  userSelect: 'all',
                  padding: '0.5rem 1rem',
                  backgroundColor: 'rgba(155, 89, 182, 0.1)',
                  borderRadius: '8px',
                  display: 'inline-block',
                  marginTop: '0.5rem'
                }}
                title="点击复制"
              >
                {status.verify_code}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#888', marginTop: '0.5rem' }}>
                点击验证码可复制
              </div>
            </div>
            
            <div style={{ marginBottom: '1rem' }}>
              <div style={labelStyle}>
                <span style={{ marginRight: '0.5rem' }}>📍</span>
                请前往直播间发送验证码
              </div>
              <button
                onClick={() => window.open(`https://live.bilibili.com/${status.verify_room_id}`, '_blank')}
                style={{
                  ...buttonStyle,
                  backgroundColor: '#00a1d6',
                  color: '#fff',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  marginTop: '0.5rem'
                }}
              >
                <span>📺</span> 前往直播间 {status.verify_room_id}
              </button>
            </div>
            
            <div style={{ 
              fontSize: '0.875rem',
              color: countdown > 30 ? '#666' : '#c00',
              fontWeight: countdown <= 30 ? '600' : '400'
            }}>
              ⏱️ 剩余时间: {countdown} 秒
            </div>
          </div>
          
          <p style={{ 
            fontSize: '0.875rem', 
            color: 'var(--text-light)',
            marginBottom: '1rem'
          }}>
            验证码已自动复制，请在 B站直播间的弹幕输入框中粘贴发送，系统将自动完成绑定。
          </p>
          
          <button 
            onClick={requestBinding}
            disabled={requesting || countdown > 0}
            style={{
              ...buttonStyle,
              backgroundColor: countdown > 0 ? '#ccc' : 'var(--primary-purple)',
              color: '#fff',
              opacity: requesting ? 0.7 : 1
            }}
          >
            {requesting ? '请求中...' : '重新获取验证码'}
          </button>
        </div>
      )}

      {/* 未绑定状态 */}
      {!status?.bound && !status?.pending && (
        <div>
          <p style={{ 
            color: 'var(--text-light)',
            marginBottom: '1rem'
          }}>
            绑定B站账号后，您可以在直播间使用更多功能。
          </p>
          
          <button 
            onClick={requestBinding}
            disabled={requesting}
            style={{
              ...buttonStyle,
              backgroundColor: 'var(--primary-purple)',
              color: '#fff',
              opacity: requesting ? 0.7 : 1
            }}
          >
            {requesting ? '请求中...' : '开始绑定'}
          </button>
        </div>
      )}
    </div>
  );
}

export default BilibiliBinding;
